import { app, dialog } from '@microsoft/teams-js';
import { ColorScheme, ThemeVariant } from '@toggle/design-system';
import { useCallback, useEffect, useState } from 'react';

import { useThemeStore } from '~/components/theme-controller/stores/useThemeStore';

export interface UseTeamsReturn {
  isInTeams: boolean;
  isInitialized: boolean;
  openTaskModule: (
    url: string,
    title?: string,
    size?: { width: number; height: number }
  ) => void;
  sendNotification: (message: string, type?: 'success' | 'failure') => void;
  context?: app.Context;
}

export const useTeams = (): UseTeamsReturn => {
  const [isInTeams, setIsInTeams] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { changeTheme } = useThemeStore();
  const [context, setContext] = useState<app.Context | undefined>(undefined);

  const initializeTeams = useCallback(async () => {
    try {
      await app.initialize();
      setIsInitialized(true);

      const ctx = await app.getContext();
      setContext(ctx);

      // Extract and set theme from context
      if (ctx.app?.theme) {
        const teamsTheme = ctx.app.theme as 'dark' | 'default' | 'contrast';
        // Map Teams theme to ColorScheme (contrast -> dark, default -> light)
        const mappedTheme =
          teamsTheme === 'default' ? ColorScheme.Light : ColorScheme.Dark;
        changeTheme(mappedTheme, ThemeVariant.Default);
      }

      // eslint-disable-next-line no-console
      console.log('Teams SDK initialized with context:', ctx);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Teams SDK:', error);
    }
  }, [changeTheme]);

  useEffect(() => {
    // Check if running in Teams
    const checkTeamsEnvironment =
      window.parent !== window.self ||
      window.name === 'embedded-page-container';
    setIsInTeams(checkTeamsEnvironment);

    // Initialize Teams SDK
    if (checkTeamsEnvironment) {
      initializeTeams();
    }
  }, [initializeTeams]);

  const openTaskModule = (
    url: string,
    title = 'View Details',
    size = { width: 800, height: 600 }
  ) => {
    if (isInTeams && isInitialized) {
      try {
        dialog.url.open({
          url,
          size,
          title,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to open task module:', error);
        // Fallback to window.open
        window.open(url, '_blank', `width=${size.width},height=${size.height}`);
      }
    } else {
      // Fallback for non-Teams environment
      window.open(url, '_blank');
    }
  };

  const sendNotification = (
    message: string,
    type: 'success' | 'failure' = 'success'
  ) => {
    if (isInTeams && isInitialized) {
      try {
        if (type === 'success') {
          app.notifySuccess();
          // eslint-disable-next-line no-console
          console.log('Teams notification (success):', message);
        } else {
          app.notifyFailure({
            reason: app.FailedReason.Other,
            message,
          });
          // eslint-disable-next-line no-console
          console.log('Teams notification (failure):', message);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send Teams notification:', error);
      }
    } else {
      // Fallback for non-Teams environment - show browser notification or log
      // eslint-disable-next-line no-console
      console.log(`Notification (${type}):`, message);
    }
  };

  return {
    isInTeams,
    isInitialized,
    openTaskModule,
    sendNotification,
    context,
  };
};
